// Minimal in-tree replacement for `connected-react-router`, now backing
// onto react-router v6.
//
// Why: connected-react-router is unmaintained and incompatible with
// react-router v6+. ~119 files in this codebase still import from it
// (sagas dispatching `push(...)`, selectors reading `getLocation`,
// `LOCATION_CHANGE` listeners, `createMatchSelector`-driven edit-id
// resolution). Rather than touch them all, vite.config.ts aliases the
// package name to this shim and the call sites stay unchanged.
//
// Surface: matches the parts of the CRR API the codebase actually
// uses — push/replace/go/goBack action creators, getLocation
// selector, createMatchSelector, LOCATION_CHANGE action type,
// connectRouter reducer, routerMiddleware, and ConnectedRouter
// component.
//
// RR6 specifics handled here:
//   - `<Router history={...}>` is gone; use unstable_HistoryRouter.
//   - history v5's listen callback receives `({ location, action })`
//     (a single object) instead of v4's `(location, action)`.
//   - `matchPath` arg order swapped: `matchPath(pattern, pathname)`
//     in v6 vs `matchPath(pathname, options)` in v5. createMatchSelector
//     hides this so consumers don't need to know.
//   - matchPath default is `end: true` (v5 was `exact: false`); for the
//     prefix-match call sites the caller must opt in via
//     `{ path: '/foo/:id', end: false }`.

import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  unstable_HistoryRouter as HistoryRouter,
  matchPath as rrMatchPath,
} from 'react-router-dom';
import { createSelector } from 'reselect';

// ---- action types ---------------------------------------------------------
export const LOCATION_CHANGE = '@@router/LOCATION_CHANGE';
export const CALL_HISTORY_METHOD = '@@router/CALL_HISTORY_METHOD';

// ---- action creators ------------------------------------------------------
const callHistory = (method) => (...args) => ({
  type: CALL_HISTORY_METHOD,
  payload: { method, args },
});

export const push = callHistory('push');
export const replace = callHistory('replace');
export const go = callHistory('go');
export const goBack = callHistory('goBack');
export const goForward = callHistory('goForward');

// ---- reducer factory ------------------------------------------------------
// `connectRouter(history)` returns a reducer keyed on LOCATION_CHANGE
// that mirrors history.location into redux state.
export const connectRouter = (history) => (
  state = { location: history.location, action: history.action },
  action,
) => {
  if (action.type === LOCATION_CHANGE) return action.payload;
  return state;
};

// ---- middleware factory ---------------------------------------------------
// Intercepts CALL_HISTORY_METHOD actions and forwards them to the
// history instance. history v5 keeps the same imperative API
// (push/replace/go/back/forward), so existing saga `put(push('/x'))`
// calls keep working.
export const routerMiddleware = (history) => () => (next) => (action) => {
  if (action?.type === CALL_HISTORY_METHOD) {
    const { method, args } = action.payload;
    if (typeof history[method] === 'function') {
      history[method](...args);
    } else if (method === 'goBack' && typeof history.back === 'function') {
      // history v5 renamed goBack/goForward to back/forward; provide
      // the alias for legacy callers.
      history.back();
    } else if (method === 'goForward' && typeof history.forward === 'function') {
      history.forward();
    }
    return undefined;
  }
  return next(action);
};

// ---- selectors ------------------------------------------------------------
export const getLocation = (state) => state.router?.location;

// matchPath in v6: `matchPath(pattern, pathname)`. Pattern is either
// a string or `{ path, end, caseSensitive }`. Default `end: true`
// (different from v5).
//
// Two extra responsibilities here vs RR6's raw matchPath:
//
// 1. v5 signature compatibility — v5 was `matchPath(pathname, options)`.
//    We keep accepting that order. v5 also defaulted to "prefix match"
//    (no `exact` flag); we map `exact: true` → `end: true` and default
//    to `end: false` for prefix-match parity.
//
// 2. Legacy path-to-regexp v1 syntax in patterns — `/foo/:id?` (optional
//    segment) and `/foo/(a|b|c)` (alternations) are unsupported by
//    path-to-regexp v6. We expand each pattern into the cartesian
//    product of concrete RR6-compatible patterns and try each.
//
// Returns the v5-shaped match object `{ params, path, url, isExact }`
// because most call sites read `.params` or check truthiness.

const expandLegacyPattern = (pattern) => {
  // Each segment may be:
  //   '/foo'           — literal
  //   '/:id'           — required param
  //   '/:id?'          — optional param   → expand to absent + present
  //   '/(a|b|c)'       — alternation      → expand per choice
  //   '/:id?/(new|edit)' — combined        → cartesian product
  // Empty input returns [''].
  let variants = [''];
  // Tokenize on '/', preserving the leading '' before the first '/'.
  const segments = pattern.split('/');
  for (const seg of segments.slice(1)) {
    if (!seg) {
      variants = variants.map((v) => `${v}/`);
      continue;
    }
    let choices;
    const altMatch = seg.match(/^\(([^)]+)\)$/);
    const optParamMatch = seg.match(/^:([A-Za-z_]\w*)\?$/);
    if (altMatch) {
      choices = altMatch[1].split('|').map((c) => `/${c}`);
    } else if (optParamMatch) {
      // Two variants: absent (empty) and present (the param).
      choices = ['', `/:${optParamMatch[1]}`];
    } else {
      choices = [`/${seg}`];
    }
    const next = [];
    for (const v of variants) {
      for (const c of choices) {
        next.push(v + c);
      }
    }
    variants = next;
  }
  // Strip duplicate slashes that appear when an optional segment is absent
  // between two literals (e.g. `/foo` + `` + `/edit` keeps as is, but a
  // sequence might leave `//`). Normalise.
  return [...new Set(variants.map((v) => v.replace(/\/{2,}/g, '/').replace(/\/$/, '') || '/'))];
};

// Signature: v5's `matchPath(pathname, optionsOrPath)` — pathname FIRST.
// v6 reversed the order to `matchPath(pattern, pathname)`; we keep v5
// order so the 70+ existing call sites work unchanged. Internally we
// convert and dispatch to rrMatchPath in its v6 form.
const callMatchPath = (pathname, optionsOrPath) => {
  // Selectors can call matchPath before the router slice has been
  // hydrated (test harnesses without a ConnectedRouter, or the very
  // first render before the LOCATION_CHANGE event lands), in which
  // case `pathname` is undefined. v6's matchPath calls
  // `pathname.match(...)` and throws on non-string. Bail to null.
  if (typeof pathname !== 'string') return null;
  const isString = typeof optionsOrPath === 'string';
  const baseOpts = isString ? { path: optionsOrPath } : { ...(optionsOrPath || {}) };
  const end = baseOpts.exact === true ? true : (baseOpts.end ?? false);
  const path = baseOpts.path;
  if (typeof path !== 'string') return null;
  const candidates = expandLegacyPattern(path);
  for (const candidate of candidates) {
    const result = rrMatchPath({ path: candidate, end, caseSensitive: baseOpts.caseSensitive }, pathname);
    if (result) {
      return {
        params: result.params,
        path,
        url: result.pathnameBase ?? result.pathname,
        isExact: result.pathname === pathname,
      };
    }
  }
  return null;
};

export { callMatchPath as matchPath };

export const createMatchSelector = (path) =>
  createSelector([getLocation], (location) => {
    if (!location) return null;
    // v5 order: pathname first, options/pattern second.
    return callMatchPath(location.pathname, path);
  });

// ---- ConnectedRouter component -------------------------------------------
// Wraps children in unstable_HistoryRouter (RR6's external-history
// router) and dispatches LOCATION_CHANGE on history changes so the
// redux router slice stays in sync.
export const ConnectedRouter = ({ history, children }) => {
  const dispatch = useDispatch();
  useEffect(() => {
    // Initial sync — `isFirstRendering: true` matches the CRR payload
    // shape; some sagas (authorization/checkAuthByCurrentPath) bail
    // unless that flag is set on the first event.
    dispatch({
      type: LOCATION_CHANGE,
      payload: {
        location: history.location,
        action: history.action,
        isFirstRendering: true,
      },
    });
    // history v5: listener receives `{ location, action }` (single
    // object), not `(location, action)` like v4.
    const unlisten = history.listen(({ location, action }) => {
      dispatch({
        type: LOCATION_CHANGE,
        payload: { location, action, isFirstRendering: false },
      });
    });
    return unlisten;
  }, [history, dispatch]);

  return React.createElement(HistoryRouter, { history }, children);
};

export default ConnectedRouter;
